import Autocomplete from '@mui/material/Autocomplete';
import CircularProgress from '@mui/material/CircularProgress';
import TextField from '@mui/material/TextField';
import { useLocalization } from '@/hooks/useLocalization';
import type { AccountTagOption } from '@/modules/portfolio/accountTags/accountTagCatalog';
import { normalizeAccountTagSelection } from '@/utils/accountGroupTagAssignments';

export interface AccountTagsAutocompleteProps {
  options: AccountTagOption[];
  selectedTagIds: number[];
  onChange: (tagIds: number[]) => void;
  isLoading?: boolean;
  disabled?: boolean;
}

/**
 * Multi-select tag picker shared by account edit (Information tab) and
 * add-account Account Details. Always renders the input so Tags remain an
 * optional, visible field even while options are loading or empty.
 */
export default function AccountTagsAutocomplete({
  options,
  selectedTagIds,
  onChange,
  isLoading = false,
  disabled = false,
}: AccountTagsAutocompleteProps) {
  const { t } = useLocalization();
  const selectedById = new Map(
    options.map((tag): [number, AccountTagOption] => [tag.value, tag]),
  );
  const selectedOptions: AccountTagOption[] = normalizeAccountTagSelection(
    selectedTagIds.flatMap((id) => {
      const option = selectedById.get(id);
      return option ? [option] : [];
    }),
  );

  return (
    <Autocomplete<AccountTagOption, true>
      multiple
      disableCloseOnSelect
      options={options}
      loading={isLoading}
      getOptionLabel={(option) => option.label}
      isOptionEqualToValue={(a, b) => a.value === b.value}
      value={selectedOptions}
      onChange={(_, newValue: AccountTagOption[]) => {
        const normalized: AccountTagOption[] =
          normalizeAccountTagSelection(newValue);
        onChange(normalized.map((option) => option.value));
      }}
      disabled={disabled || isLoading}
      noOptionsText={isLoading ? t('loading') : t('filters.noTagsAvailable')}
      renderInput={(params) => {
        const { InputProps, ...textFieldParams } = params;
        return (
          <TextField
            {...textFieldParams}
            label={t('editAccountModal.tagsOptional')}
            placeholder={t('editAccountModal.tagSearchPlaceholder')}
            slotProps={{
              input: {
                ...InputProps,
                endAdornment: (
                  <>
                    {isLoading ? (
                      <CircularProgress
                        color='inherit'
                        size={20}
                      />
                    ) : null}
                    {InputProps.endAdornment}
                  </>
                ),
              },
            }}
          />
        );
      }}
      slotProps={{
        popper: {
          sx: { zIndex: (theme) => theme.zIndex.modal + 1 },
        },
      }}
    />
  );
}
